import { Address } from '../types/address';
import { getFullLineAddress } from '../utils/get-full-line-address';
import { Label } from '@/shadcn-ui/label';
import { RadioGroup, RadioGroupItem } from '@/shadcn-ui/radio-group';
import { useEffect, useState } from 'react';
import AddressFormFragment, { AddressFormFragmentProps } from './address-form-fragment';
import HiddenAddressFormInputs from './hidden-address-form-inputs';

export interface BillingAddressFormFragmentProps extends AddressFormFragmentProps {
  defaultAddress?: Address;
}

export default function BillingAddressFormFragment({
  onAddressChange,
  defaultAddress,
  addressErrors,
  smartyStreetsEmbeddedKey,
}: BillingAddressFormFragmentProps) {
  const [addressInUse, setAddressInUse] = useState<string>(
    defaultAddress ? 'default' : 'new'
  );

  // Set the initial address value when the component mounts
  useEffect(() => {
    if (defaultAddress && addressInUse === 'default' && onAddressChange) {
      onAddressChange(defaultAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddressTypeChange = (value: string) => {
    setAddressInUse(value);

    if (onAddressChange) {
      if (value === 'default' && defaultAddress) {
        // When switching to default address, pass the complete default address
        onAddressChange(defaultAddress);
      }
      // When switching to new address, reset address fields
      else if (value === 'new') {
        onAddressChange({
          address1: undefined,
          address2: undefined,
          city: undefined,
          state: undefined,
          zipcode: undefined,
        });
      }
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="body-strong">Billing address</h3>

      {defaultAddress ? (
        <>
          <RadioGroup
            name="plan"
            onValueChange={handleAddressTypeChange}
            value={addressInUse}
            defaultValue={addressInUse}
            className="gap-2"
          >
            <Label className="body flex cursor-pointer flex-row items-center justify-start gap-2 rounded-md">
              <RadioGroupItem
                value="default"
                selected={addressInUse === 'default'}
                id="default_address"
              />
              <p className="fs-mask body">{getFullLineAddress(defaultAddress)}</p>
            </Label>

            <Label className="body flex cursor-pointer flex-row items-center justify-start gap-2 rounded-md">
              <RadioGroupItem
                selected={addressInUse === 'new'}
                value="new"
                id="new_address"
              />
              <p className="body">Use a different address</p>
            </Label>
          </RadioGroup>

          {addressInUse === 'default' && (
            <HiddenAddressFormInputs address={defaultAddress} />
          )}
        </>
      ) : null}

      {(!defaultAddress || addressInUse === 'new') && (
        <AddressFormFragment
          onAddressChange={onAddressChange}
          smartyStreetsEmbeddedKey={smartyStreetsEmbeddedKey}
          addressErrors={addressErrors}
        />
      )}
    </div>
  );
}
